import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useSession } from '@/features/auth';
import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { setLanguage, SUPPORTED_LANGUAGES } from '@/shared/i18n/i18n';
import type { Language } from '@/shared/i18n/i18n';
import { useThemeStore } from '@/shared/theme/themeStore';
import type { ThemePreference } from '@/shared/theme/themeStore';
import { Button, Field, useToastStore } from '@/shared/ui';

import { useUpdateOwnDisplayName } from '../queries/useIdentity';
import styles from './Settings.module.css';
import { SectionHead } from './SettingsScreen';

/**
 * Moje konto — jedyna sekcja dostępna dla **każdego**, nie tylko admina.
 *
 * ⭐ **Nazwę wyświetlaną zmienia się samodzielnie** — to jest ta „klasyczna rzecz", którą
 * zwykły użytkownik ma dostać. ⚠ **Login i hasło zostają u admina** (spec sek. 10: bez maili,
 * bez samoobsługowego resetu), więc stoją tu jako odczyt, a sekcja mówi dlaczego — zamiast
 * pokazywać pola, które i tak zostałyby odrzucone.
 *
 * Poza tym ustawialne jest to, co należy do **tej przeglądarki**: motyw i język.
 */

const THEMES: readonly ThemePreference[] = ['system', 'light', 'dark'];

export function AccountSection() {
  const { t, i18n } = useTranslation();
  const { data: user } = useSession();
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);

  const language = (i18n.resolvedLanguage ?? 'pl') as Language;

  return (
    <>
      <SectionHead titleKey="settings.section.account" leadKey="settings.account.lead" />

      <section className={styles['card']}>
        <header className={styles['cardHead']}>
          <h2 className={styles['cardTitle']}>{t('settings.account.profile')}</h2>
        </header>

        <div className={styles['cardBody']}>
          {/*
            ⚠ `key` na wartości, nie na niczym: pole trzyma nazwę w stanie lokalnym, a stan
            inicjuje się RAZ. Bez klucza pierwszy render (przed dojściem sesji) ustawiłby
            pustą nazwę i już nigdy by się nie zsynchronizował. Przy okazji daje darmowy
            reset pola po udanym zapisie.
          */}
          <DisplayNameForm key={user?.displayName ?? ''} current={user?.displayName ?? ''} />

          <div className={styles['row']}>
            <span className={styles['rowLabel']}>{t('settings.account.login')}</span>
            <span className={styles['rowValue']}>{user?.login ?? '—'}</span>
          </div>

          <div className={styles['row']}>
            <span className={styles['rowLabel']}>{t('settings.account.teams')}</span>
            <span className={styles['rowValue']}>
              {user && user.teams.length > 0
                ? user.teams.map((team) => team.name).join(' · ')
                : t('settings.account.noTeams')}
            </span>
          </div>

          <div className={styles['row']}>
            <span className={styles['rowLabel']}>{t('settings.account.roles')}</span>
            <span className={styles['rowValue']}>
              {user?.roles.map((role) => t(`settings.account.role.${role}`, { defaultValue: role })).join(' · ')}
            </span>
          </div>
        </div>
      </section>

      <section className={styles['card']}>
        <header className={styles['cardHead']}>
          <h2 className={styles['cardTitle']}>{t('settings.account.appearance')}</h2>
        </header>

        <div className={styles['cardBody']}>
          <div className={styles['row']}>
            <span className={styles['rowLabel']}>{t('settings.account.theme')}</span>
            <span className={styles['choices']}>
              {THEMES.map((option) => (
                <Button
                  key={option}
                  variant={preference === option ? 'primary' : 'ghost'}
                  onClick={() => setPreference(option)}
                >
                  {t(`settings.account.themeOption.${option}`)}
                </Button>
              ))}
            </span>
          </div>

          <div className={styles['row']}>
            <span className={styles['rowLabel']}>{t('settings.account.language')}</span>
            <span className={styles['choices']}>
              {SUPPORTED_LANGUAGES.map((option) => (
                <Button
                  key={option}
                  variant={language === option ? 'primary' : 'ghost'}
                  onClick={() => setLanguage(option)}
                >
                  {t(`settings.account.languageOption.${option}`)}
                </Button>
              ))}
            </span>
          </div>
        </div>
      </section>
    </>
  );
}

/**
 * Zmiana własnej nazwy wyświetlanej.
 *
 * Odpowiedź serwera to pełne „kim jestem", więc trafia wprost do pamięci podręcznej sesji —
 * nazwa stoi w powłoce i przy właścicielach, a ponowne odpytanie dałoby mignięcie starą.
 */
function DisplayNameForm({ current }: { current: string }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const update = useUpdateOwnDisplayName();

  const [value, setValue] = useState(current);
  const [touched, setTouched] = useState(false);

  const error = touched && value.trim() === '' ? t('settings.account.nameRequired') : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (value.trim() === '' || value.trim() === current) {
      return;
    }

    update.mutate(value.trim(), {
      onSuccess: () => show({ message: t('settings.account.saved'), tone: 'success' }),
      onError: (failure) => show({ message: describeError(failure), tone: 'danger' }),
    });
  }

  return (
    <form className={styles['inlineForm']} onSubmit={onSubmit} noValidate>
      <span className={styles['grow']}>
        <Field
          label={t('settings.account.displayName')}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          {...(error ? { error } : {})}
        />
      </span>

      <Button type="submit" variant="ghost" disabled={update.isPending}>
        {t('settings.account.save')}
      </Button>
    </form>
  );
}
